import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { FormUtils, InvalidLabel, State } from 'ats-ui-lib';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environments/environment';
import {
  ApplicantFieldValidationCustom,
  ApplicationRequest,
  FieldIdentifierEnum,
  IdResult,
  JobApplicationCreation,
  JobOpportunity,
  JobOpportunityTitleStatus,
} from '@models/index';

@Injectable({
  providedIn: 'root',
})
export class JobOpportunityApplicationService extends State<ApplicantFieldValidationCustom[]> {
  private labelAnd: string | undefined;

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
  ) {
    super([]);
  }

  public allInfo(jobOpportunityId: string, tenantId: string): Observable<JobOpportunity> {
    return this.http.get<JobOpportunity>(
      `${environment.applicantApiEndpoint}/job-opportunity/all-info/${jobOpportunityId}/${tenantId}`,
    );
  }

  public titleStatus(
    jobOpportunityId: string,
    tenantId: string,
  ): Observable<JobOpportunityTitleStatus> {
    return this.http.get<JobOpportunityTitleStatus>(
      `${environment.applicantApiEndpoint}/job-opportunity/title-status/${jobOpportunityId}/${tenantId}`,
    );
  }

  public getJobOpportunity(jobOpportunityId: string, tenantId: string): Observable<JobOpportunity> {
    return this.http.get<JobOpportunity>(
      `${environment.applicantApiEndpoint}/job-opportunity/${jobOpportunityId}/${tenantId}`,
    );
  }

  public getCheckApplication(
    jobOpportunityId: string,
    applicantId: string | undefined,
  ): Observable<boolean> {
    return this.http
      .get<{
        exists: boolean;
      }>(
        `${environment.applicantApiEndpoint}/jobapplications/applicationExists/${jobOpportunityId}/${applicantId}`,
      )
      .pipe(map((result) => result.exists));
  }

  public postJobApplication(jobApplication: JobApplicationCreation): Observable<IdResult> {
    return this.http.post<IdResult>(
      `${environment.applicantApiEndpoint}/api/v2/jobapplications/application-request`,
      jobApplication,
    );
  }

  public getJobApplicationRequest(id: string): Observable<ApplicationRequest> {
    return this.http.get<ApplicationRequest>(
      `${environment.applicantApiEndpoint}/jobapplications/application-request/${id}`,
    );
  }

  async getApplicantFieldsValidation(
    jobOpportunityId?: string,
    tenantId?: string,
  ): Promise<ApplicantFieldValidationCustom[]> {
    const defaultFieldsValidation = this.getJobOpportunityFieldsValidation();
    if (jobOpportunityId && tenantId) {
      try {
        const jobOpportunity = await lastValueFrom(
          this.getJobOpportunity(jobOpportunityId, tenantId),
        );
        if (jobOpportunity) {
          defaultFieldsValidation.forEach((field) => {
            const fieldValidation = jobOpportunity.jobOpportunityFieldValidationCollection.filter(
              (f) => f.fieldIdentifier === field.fieldIdentifier,
            );
            if (fieldValidation && fieldValidation.length > 0)
              field.isRequired = fieldValidation[0].isRequired;
          });
        }

        this.setState(defaultFieldsValidation);
        return defaultFieldsValidation;
      } catch {
        this.setState(defaultFieldsValidation);
        return defaultFieldsValidation;
      }
    } else {
      this.setState(defaultFieldsValidation);
      return defaultFieldsValidation;
    }
  }

  private getJobOpportunityFieldsValidation(): ApplicantFieldValidationCustom[] {
    return [
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.SocialName,
        fieldDescription: 'socialName',
        fieldGroupDescription: 'firstStep',
      },
      {
        isRequired: true,
        fieldIdentifier: FieldIdentifierEnum.FullName,
        fieldDescription: 'fullName',
        fieldGroupDescription: 'firstStep',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.Cpf,
        fieldDescription: 'cpf',
        fieldGroupDescription: 'firstStep',
      },
      {
        isRequired: true,
        fieldIdentifier: FieldIdentifierEnum.Email,
        fieldDescription: 'email',
        fieldGroupDescription: 'firstStep',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.TelephoneNumber,
        fieldDescription: 'telephoneNumber',
        fieldGroupDescription: 'firstStep',
      },
      {
        isRequired: true,
        fieldIdentifier: FieldIdentifierEnum.Birthday,
        fieldDescription: 'birthday',
        fieldGroupDescription: 'firstStep',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.AddressZipCode,
        fieldDescription: 'cep',
        fieldGroupDescription: 'firstStep',
        fieldPath: 'address.cep',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.AddressState,
        fieldDescription: 'stateId',
        fieldGroupDescription: 'firstStep',
        fieldPath: 'address.stateId',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.AddressCity,
        fieldDescription: 'cityId',
        fieldGroupDescription: 'firstStep',
        fieldPath: 'address.cityId',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.AddressStreet,
        fieldDescription: 'street',
        fieldGroupDescription: 'firstStep',
        fieldPath: 'address.street',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.AddressComplement,
        fieldDescription: 'complement',
        fieldGroupDescription: 'firstStep',
        fieldPath: 'address.complement',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.ProfessionalExperience,
        fieldDescription: 'professionalExperience',
        fieldGroupDescription: 'secondStep.resume',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.ProfessionalExperienceDescription,
        fieldDescription: 'professionalExperienceDescription',
        fieldGroupDescription: 'secondStep.resume',
        fieldPath: 'description',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.Qualification,
        fieldDescription: 'qualifications',
        fieldGroupDescription: 'thirdStep.resume',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.AdditionalInformationPortfolio,
        fieldDescription: 'portfolioUrl',
        fieldGroupDescription: 'fourthStep',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.AdditionalInformationLinkedin,
        fieldDescription: 'linkedinUrl',
        fieldGroupDescription: 'fourthStep',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.Goal,
        fieldDescription: 'professionalPurpose',
        fieldGroupDescription: 'fifthStep',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.GoalSalaryRange,
        fieldDescription: 'salaryRange',
        fieldGroupDescription: 'fifthStep',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.ExtraInformationJobApplication,
        fieldDescription: 'extraInformation',
        fieldGroupDescription: 'fifthStep',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.Profession,
        fieldDescription: 'profession',
        fieldGroupDescription: '',
        fieldPath: 'profession',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.FixPhoneNumber,
        fieldDescription: 'fixPhoneNumber',
        fieldGroupDescription: '',
        fieldPath: 'fixPhoneNumber',
      },
      {
        isRequired: false,
        fieldIdentifier: FieldIdentifierEnum.Languages,
        fieldDescription: 'languages',
        fieldGroupDescription: '',
        fieldPath: 'languages',
      },
    ];
  }

  public async getInvalidMessage(
    formGroups: UntypedFormGroup[],
    messageTranslating: string,
    isDesktop: boolean = true,
  ): Promise<string> {
    const formGroupsInvalids = formGroups.filter((formGroup) => formGroup.invalid);
    if (formGroupsInvalids.length === 0) {
      return '';
    }

    let invalidLabels: InvalidLabel[] = [];

    formGroupsInvalids.forEach((formGroup) => {
      invalidLabels.push(...FormUtils.getInvalidLabels(formGroup));
    });

    const invalidLabelsTranslating = invalidLabels
      .filter((invalidLabel) => invalidLabel.isEmpty)
      .map((invalidLabel) => invalidLabel.label);
    if (invalidLabelsTranslating.length === 0) {
      return '';
    }

    const invalidLabelsTranslatingDistinct = invalidLabelsTranslating.filter(
      (value, index, self) => self.indexOf(value) === index,
    );

    const messageInPlural = invalidLabelsTranslatingDistinct.length > 1;

    if (!isDesktop) {
      messageTranslating = `l-required-control${messageInPlural ? 's' : ''}`;
      return lastValueFrom(this.translateService.get(messageTranslating));
    }

    if (messageInPlural) {
      messageTranslating += 's';
    }

    const invalidLabelsTranslated = await this.translateOrDefault(invalidLabelsTranslatingDistinct);

    this.labelAnd = this.labelAnd ?? (await lastValueFrom(this.translateService.get('l-and')));

    const labels = invalidLabelsTranslated
      .map((label) => `"${label}"`)
      .join(', ')
      .replace(/,([^,]*)$/, ` ${this.labelAnd}$1`);

    return lastValueFrom(this.translateService.get(messageTranslating, { labels: labels }));
  }

  private async translateOrDefault(keys: string[]): Promise<string[]> {
    var values: string[] = [];
    keys.forEach(async (key) => {
      try {
        const value = await lastValueFrom(this.translateService.get(key));
        values.push(value);
      } catch {
        values.push(key);
      }
    });
    return values;
  }
}
